import { BiEnvelope, BiBulb } from 'react-icons/bi';

// Help topics rendered on /help. Each entry:
//   id     — URL slug (`/help/<id>`); kebab-case
//   label  — sidebar label
//   render — function returning the right-hand panel JSX
//
// Keep copy short (2-4 sentences per section is the target). Tone is
// friendly and jargon-free — most users are sole-trader tradies, not
// SaaS power users. If a workflow has more than ~3 steps, use a small
// ordered list rather than burying it in prose.

const SUPPORT_EMAIL = 'jordan@wearecodira.com';

const StillNeedHelp = () => (
    <>
        <p>If you can't find what you need above, drop us a line. Real humans read every message and we usually reply the same day.</p>
        <div className="d-flex flex-wrap gap-2">
            <a
                href={`mailto:${SUPPORT_EMAIL}?subject=MarginPal%20Support`}
                className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
            >
                <BiEnvelope size={18} />
                Contact Support
            </a>
            <a
                href={`mailto:${SUPPORT_EMAIL}?subject=MarginPal%20Suggestion`}
                className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
            >
                <BiBulb size={18} />
                Make a Suggestion
            </a>
        </div>
    </>
);

export const HELP_TOPICS = [
    {
        id: 'getting-started',
        label: 'Getting started',
        render: () => (
            <>
                <p>MarginPal helps you keep an eye on the margin on every job — quoted vs what it actually costs you. The shortest path through the app:</p>
                <ol className="ps-3 mb-3">
                    <li>Add a <strong>customer</strong>.</li>
                    <li>Create a <strong>job</strong> for the work.</li>
                    <li>Send a <strong>quote</strong> for what you'll charge.</li>
                    <li>As work progresses, log <strong>expenses</strong> (manually or by scanning receipts) and upload <strong>invoices</strong> from your suppliers.</li>
                    <li>Watch the <strong>dashboard</strong> for live margin and any jobs going over budget.</li>
                </ol>
                <p>If you'd rather poke around freely, the sidebar walks left-to-right roughly in workflow order — Dashboard, Customers, Quotes, Jobs.</p>
            </>
        ),
    },
    {
        id: 'dashboard',
        label: 'Dashboard',
        render: () => (
            <>
                <p>Your home page. Cards show this month's revenue, costs, profit, margin %, active jobs, and any jobs over budget. The charts below break down profit over time, expense categories, and revenue vs cost.</p>
                <p>Drag any card by its handle to rearrange the layout — your arrangement is saved in your browser. Hit <em>Reset layout</em> on the dashboard if you want the defaults back. On smaller screens the cards stack and dragging is disabled.</p>
                <p>If you're an admin and haven't set up your branding yet, you'll see a small checklist nudging you through that — once done, it disappears.</p>
            </>
        ),
    },
    {
        id: 'customers',
        label: 'Customers',
        render: () => (
            <>
                <p>The people you do work for. Add a customer once and you can attach them to as many jobs and quotes as you need.</p>
                <p>The address details (town, postcode, country) flow through onto quote PDFs (Pro and Business), so it's worth filling them in even if your records are otherwise minimal. Click any row to see that customer's history.</p>
            </>
        ),
    },
    {
        id: 'quotes',
        label: 'Quotes',
        render: () => (
            <>
                <p>Quotes are how you tell a customer what a job will cost before you start. On any tier, you can create quotes, add line items, and keep them organised in the app — useful for your own records and for converting an accepted quote into a job.</p>
                <p>The customer-facing flow is a <strong>Pro and Business</strong> feature, and unlocks three things together:</p>
                <ul className="ps-3 mb-3">
                    <li><strong>Branded PDFs</strong> — pick from five templates (configurable in <em>Branding</em>), then download or email.</li>
                    <li><strong>Email send</strong> — fire the quote off to your customer's inbox without leaving the app.</li>
                    <li><strong>Accept / Decline</strong> — the emailed link lets the customer respond from a public page, no login needed; their answer lands on the quote in real time.</li>
                </ul>
                <p>Once a quote is accepted, <strong>convert it to a job</strong> with a single click on any tier — the quoted total becomes the job's budget so you can track over/under as work progresses.</p>
            </>
        ),
    },
    {
        id: 'jobs',
        label: 'Jobs',
        render: () => (
            <>
                <p>The actual work. Each job tracks <strong>quoted</strong> figures (from a converted quote, or what you entered manually) against <strong>actual</strong> figures. Actuals come from two places: <em>expenses</em> roll up to the cost side, <em>invoices</em> roll up to the revenue side. The gap between the two is your live margin.</p>
                <p>Job status moves: <em>Open</em> → <em>In progress</em> → <em>Completed</em>. Use <em>On hold</em> when work is paused, <em>Cancelled</em> if it's not happening. Status drives the dashboard's "active jobs" count and what shows up in over-budget alerts.</p>
                <p>Open any job to attach expenses, upload invoices, or update what's been quoted.</p>
            </>
        ),
    },
    {
        id: 'receipts-expenses',
        label: 'Receipts & expenses',
        render: () => (
            <>
                <p>Two ways to log a cost on a job:</p>
                <ol className="ps-3 mb-3">
                    <li><strong>Manual entry</strong> — type in the supplier, amount, date, and category. Fastest if you've got the figures already.</li>
                    <li><strong>Receipt scanning</strong> — upload a photo or PDF and our AI reads it for you, filling in the fields. You'll get a chance to check before saving.</li>
                </ol>
                <p>Each plan includes a monthly scan allowance (75/250/600 for Solo/Pro/Business). Once you reach your limit, scanning is paused until your allowance resets at the start of the next billing period — upgrade your plan if you need more. Manual entry is always free and never counts against the allowance.</p>
            </>
        ),
    },
    {
        id: 'invoices',
        label: 'Invoices',
        render: () => (
            <>
                <p>Invoices are your record of money coming in on a job — what you've billed (or been paid) for the work. Each invoice attaches to a specific job, carries an amount and optional VAT, and rolls up into that job's <strong>actual revenue</strong>. They sit on the revenue side of the margin maths, opposite expenses.</p>
                <p>Two ways to log one — same workflow as expenses:</p>
                <ol className="ps-3 mb-3">
                    <li><strong>Manual entry</strong> — type in the date, amount, VAT, and a category.</li>
                    <li><strong>Scan upload</strong> — snap a photo or PDF and the AI fills in the fields for you.</li>
                </ol>
                <p>Tag each invoice with an <em>Invoice category</em> so the revenue side breaks down cleanly in reports. Invoice scans count against the same monthly scan allowance as receipts — one shared bucket of OCR work.</p>
                <p>Worth saying what invoices <em>aren't</em>: they aren't customer-facing PDFs you generate from MarginPal. They're records of invoicing you've done elsewhere (accounting software, paper, etc.), kept here so your job-level margin is real.</p>
            </>
        ),
    },
    {
        id: 'integrations',
        label: 'Integrations',
        render: () => (
            <>
                <p><em>Pro and Business.</em> Connect MarginPal to your accounting package so invoices and expenses flow straight through — no more double entry at bookkeeping time. <strong>Xero</strong> is available now; <strong>QuickBooks</strong> and <strong>Sage</strong> are coming soon.</p>
                <p><strong>Connecting:</strong> go to <em>Settings → Accounting integrations</em> and hit <em>Connect to Xero</em>. You'll sign in on Xero's own site and approve access (we never see your password), then choose which organisation to link.</p>
                <p>Once connected:</p>
                <ul className="ps-3 mb-3">
                    <li><strong>Invoices &amp; expenses</strong> push to Xero as sales invoices and bills, with the receipt or document attached. With <strong>Auto Sync</strong> on (the default) they go automatically as you add them; switch it off to push manually with the <em>Send</em> button on each record.</li>
                    <li><strong>Customers</strong> sync as contacts, matched by email so you don't get duplicates. <em>Sync customers</em> does a two-way tidy-up — imports your Xero customers and pushes yours up.</li>
                    <li><strong>Payment status</strong> flows back: mark an invoice paid in Xero and it locks in MarginPal so it won't be overwritten.</li>
                </ul>
                <p>Set your default ledger accounts and VAT codes in the same settings area so pushed records land in the right place. You can disconnect any time — already-synced records stay in your accounting system.</p>
            </>
        ),
    },
    {
        id: 'ai-insights',
        label: 'AI insights',
        render: () => (
            <>
                <p><em>Business tier only.</em> A small AI assistant that sweeps your data daily and writes plain-English notes about what's going on — margin trends, jobs trending over budget, suppliers eating into profit, that sort of thing.</p>
                <p>Insights show up in two places: <strong>Dashboard insights</strong> (overall business view) refresh once a day, and <strong>Job insights</strong> on each job page refresh whenever you upload new costs against that job.</p>
            </>
        ),
    },
    {
        id: 'branding',
        label: 'Branding',
        render: () => (
            <>
                <p>How your quotes look to customers. These settings power the quote PDFs, which are a <strong>Pro and Business</strong> feature — on Solo you can fill them in, but they only come into play once you upgrade. Set up in <em>Settings → Branding</em>:</p>
                <ul className="ps-3 mb-3">
                    <li><strong>Logo</strong> — appears on every PDF.</li>
                    <li><strong>Brand colour</strong> — used for accents on the templates that support colour.</li>
                    <li><strong>Contact details</strong> — your address, phone, email, website, all shown in the PDF footer.</li>
                    <li><strong>Quote template</strong> — five styles (Classic, Modern, Corporate, Formal, Trade). Pick the one that matches your brand. Toggle the signature area on if you want a printed sign-off block.</li>
                </ul>
                <p>Changes are live the next time you generate a PDF — no need to re-save anything.</p>
            </>
        ),
    },
    {
        id: 'team-roles',
        label: 'Team & roles',
        render: () => (
            <>
                <p>Invite your team in <em>Settings → Members</em>. Two roles:</p>
                <ul className="ps-3 mb-3">
                    <li><strong>Admin</strong> — full access including settings, billing, members, and branding.</li>
                    <li><strong>Member</strong> — can use the app day-to-day (jobs, quotes, expenses) but can't change settings or billing.</li>
                </ul>
                <p>Each tier has a seat limit (Solo: 3, Pro: 10, Business: 20). If you hit the limit, you can either remove an existing member or upgrade your plan in <em>Settings → Billing</em>. Pending invitations also count against the limit until they're accepted or revoked.</p>
            </>
        ),
    },
    {
        id: 'billing',
        label: 'Billing & subscription',
        render: () => (
            <>
                <p>Live in <em>Settings → Billing</em>. Shows your current tier, scans used this month, and your period reset date.</p>
                <p><strong>Changing plans:</strong> Upgrades apply immediately and are pro-rated. Downgrades are scheduled for the end of your current billing period — you keep your current tier's features until then, and a banner on the billing card shows when the change kicks in. You can cancel a scheduled downgrade any time before it activates.</p>
                <p><strong>Scan limits:</strong> Each tier includes a fixed monthly scan allowance. Once you reach it, scanning is paused until your allowance resets next period — upgrade if you need more. Manual expense and invoice entry is always free.</p>
            </>
        ),
    },
    {
        id: 'categories',
        label: 'Categories & cost types',
        render: () => (
            <>
                <p>Three customisable lists in <em>Settings</em>, all working the same way — add, rename, reorder, deactivate:</p>
                <ul className="ps-3 mb-3">
                    <li><strong>Expense categories</strong> — tag receipts and manual expenses (e.g. <em>Materials</em>, <em>Fuel</em>, <em>Subcontractor</em>). Drive the dashboard's expense breakdown chart.</li>
                    <li><strong>Invoice categories</strong> — tag invoices you've raised (e.g. <em>Labour</em>, <em>Callout fee</em>, <em>Materials</em>). Drive the revenue side of margin reports.</li>
                    <li><strong>Quote line item types (cost types)</strong> — what each row on a quote represents (e.g. <em>Labour</em>, <em>Materials</em>, <em>Travel</em>). Customers see these on the PDF, so name them in their language.</li>
                </ul>
                <p>You start with a sensible default list — tweak it once and forget about it.</p>
            </>
        ),
    },
    {
        id: 'still-need-help',
        label: 'Still need help?',
        render: StillNeedHelp,
    },
];
